import { motion } from "framer-motion";
import { CheckCircle2, Camera, Info, Bell } from "lucide-react";
import { useHeroScroll } from "@/components/Parallax";
import { Logo } from "@/components/Logo";
import { ConsultantRow, Progress } from "@/pages/ClientPortal";

const BORDEAUX = "#7A1F3D";
const btn = "inline-flex items-center justify-center gap-1 text-[11px] font-medium px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-ec-green shrink-0";
const btnDark = "inline-flex items-center justify-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-ec-green text-white shrink-0";
const ITEMS = [
  ["bg-red-600", "Certidão permanente caducada", "Validade terminou a 12/03 · pedir nova antes da escritura", "Proprietário", "Pedir ao proprietário", true],
  ["bg-amber-500", "Caderneta predial incompleta", "Falta a página 2 · artigo U-4821", "Proprietário", "Pedir ao proprietário", true],
  ["bg-slate-300", "Licença de utilização por rever", "Chegou hoje pela página do cliente", "Gestora", "Rever", false],
  ["bg-amber-400", "Situações especiais por responder: Herança", "Bloqueia o «Pronto para escritura»", "Proprietário", "Responder", false],
];

function TratarScreen() {
  return (
    <div className="bg-ec-bg h-full">
      <div className="bg-white border-b border-slate-100 px-4 py-2 flex items-center gap-5">
        <Logo size={16} />
        {["Processos", "Hoje", "Direção", "Contactos"].map((n, i) => <span key={n} className={`text-[10px] font-semibold ${i === 0 ? "text-ec-green" : "text-slate-400"}`}>{n}</span>)}
      </div>
      <div className="px-5 pt-4">
        <span className="font-mono text-[10px] text-ec-gold">CA-2026-031</span>
        <div className="flex items-end justify-between">
          <h3 className="font-heading text-lg text-ec-green leading-tight">Rua das Gaivotas, 14 · Cascais</h3>
          <span className="font-mono text-ec-green text-[11px]">32 dias à escritura</span>
        </div>
        <div className="mt-3 bg-white rounded-[12px] shadow-[0_8px_30px_rgb(0,0,0,0.05)] border border-slate-100 overflow-hidden">
          <h4 className="font-heading text-base text-ec-green px-4 pt-3 pb-1">A tratar · {ITEMS.length}</h4>
          <div className="divide-y divide-slate-100">
            {ITEMS.map(([bar, t, s, who, a, dark]) => (
              <div key={t} className="relative flex items-center gap-3 pl-4 pr-3 py-2.5">
                <span className={`absolute left-0 top-2 bottom-2 w-1 rounded-r ${bar}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-[11.5px] font-semibold text-ec-green truncate">{t}</div>
                  <div className="text-[10px] text-slate-500 truncate">{s}</div>
                </div>
                <span className="text-[10px] text-slate-500 w-16 shrink-0">{who}</span>
                <span className={dark ? btnDark : btn}>{dark && <Bell size={10} />}{a}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function PortalScreen() {
  return (
    <div className="bg-slate-50 w-[375px] h-[808px] origin-top-left scale-[0.509]" style={{ "--brand": BORDEAUX }}>
      <div className="bg-white border-b-2 px-5 pt-10 pb-3 flex items-center gap-3" style={{ borderColor: BORDEAUX }}>
        <span className="font-heading text-[20px] font-semibold leading-[1.05]" style={{ color: BORDEAUX }}>Casa<br />Atlântico</span>
        <span className="w-px h-8 bg-slate-300" />
        <span className="font-heading text-[20px] text-ec-green">A sua venda</span>
      </div>
      <div className="px-3 py-5 space-y-5">
        <div className="rounded-2xl p-4 text-white" style={{ backgroundColor: BORDEAUX }}>
          <ConsultantRow c={{ name: "Marta Figueiredo", phone: "912000000" }} />
          <div className="font-heading text-2xl leading-tight">Olá, Joana</div>
          <div className="text-sm opacity-75 mt-0.5">Rua das Gaivotas, 14, Cascais</div>
          <p className="text-[17px] leading-snug mt-3">Faltam 2 documentos para pôr o seu imóvel à venda.</p>
          <Progress sent={4} total={6} />
        </div>
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Os seus documentos</div>
          <div className="mt-3 space-y-3">
            <div className="rounded-xl border border-green-100 bg-green-50 px-4 py-3">
              <div className="flex items-center gap-1.5 font-semibold text-green-800"><CheckCircle2 size={16} /> Caderneta predial</div>
              <div className="text-sm text-green-800/70">Recebido · confirmado</div>
            </div>
            <div className="bg-white rounded-xl border border-slate-100 p-4">
              <div className="flex items-center justify-between font-semibold" style={{ color: BORDEAUX }}>Certidão permanente <Info size={20} className="text-slate-400" /></div>
              <div className="mt-2 text-sm rounded-lg bg-red-50 text-red-700 px-3 py-2">A certidão expirou. Peça uma nova online.</div>
              <div className="mt-3 h-11 rounded-lg text-white font-semibold text-sm flex items-center justify-center gap-2" style={{ backgroundColor: BORDEAUX }}><Camera size={16} /> Enviar outra vez</div>
              <div className="mt-2 text-center text-sm text-slate-500 underline underline-offset-2 py-1">Como pedir a certidão?</div>
            </div>
            <div className="bg-white rounded-xl border border-slate-100 p-4">
              <div className="flex items-center justify-between font-semibold" style={{ color: BORDEAUX }}>Licença de utilização <Info size={20} className="text-slate-400" /></div>
              <div className="mt-3 h-11 rounded-lg text-white font-semibold text-sm flex items-center justify-center gap-2" style={{ backgroundColor: BORDEAUX }}><Camera size={16} /> Fotografar ou enviar PDF</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function HeroMockup() {
  const laptopY = useHeroScroll([0, 40]);
  const phoneY = useHeroScroll([0, -90]);
  return (
    <div className="relative h-[275px] sm:h-[395px] lg:h-[430px] pointer-events-none select-none ec-fade-up" aria-label="ImoDesk: ecrã A tratar e página do cliente no telemóvel" role="img" data-testid="hero-mockup">
      <div className="absolute left-0 top-0 w-[640px] h-[525px] origin-top-left scale-[0.52] sm:scale-75 lg:scale-[0.82]">
        <motion.div style={{ y: laptopY }} className="absolute left-0 top-0 w-[560px]" data-testid="hero-mockup-laptop">
          <div className="rounded-t-[14px] bg-[#1c2437] p-2 pb-0 shadow-[0_30px_60px_rgba(19,34,74,0.22)]">
            <div className="h-[330px] rounded-t-md overflow-hidden"><TratarScreen /></div>
          </div>
          <div className="h-3 bg-gradient-to-b from-[#c9ced8] to-[#a9afbb] rounded-b-[12px] -mx-5" />
        </motion.div>
        <motion.div style={{ y: phoneY }} className="absolute left-[420px] top-[85px] w-[205px] h-[425px] rounded-[30px] bg-[#0f1424] p-[7px] shadow-[0_30px_60px_rgba(19,34,74,0.35)]" data-testid="hero-mockup-phone">
          <div className="relative h-full rounded-[24px] overflow-hidden bg-white">
            <span className="absolute top-1.5 left-1/2 -translate-x-1/2 w-16 h-4 rounded-full bg-[#0f1424] z-10" />
            <PortalScreen />
          </div>
        </motion.div>
      </div>
    </div>
  );
}
